/**
 * @file BlockPanel.tsx
 * @description Панель выбранного блока в конструкторе (phase19.16 Р1-6а;
 * uiux-builder-shell.md §2.1): редактор секции и защита несохранённых правок:
 *  - переход к другому блоку, разделу или странице спрашивает «Сохранить /
 *    Не сохранять / Остаться»; «назад» браузера и уход из конструктора —
 *    окно `UnsavedGuard`;
 *  - перечитанная страница (правка соседнего блока, текст на канве) не
 *    сбрасывает правки формы: тот же блок с тем же содержимым редактор не
 *    видит как новый, а изменённый на сервере при правках — только с
 *    окно «Блок изменили» (правки остаются в форме).
 *
 * phase19.16 Р2: секция v2 — `SectionBlockEditor` (вкладки «Содержимое» и
 * «Оформление», элементы, проверка, живая канва). «Чистка» (TD-327, SB107):
 * прежних форм нет — неподдерживаемый блок из данных можно только удалить.
 * Окно «Блок не сохранён» и его регистрация в конструкторе — общие с
 * «Настройками страницы» (`usePanelLeaveGuard`, `LeaveConfirmDialog`).
 */

import { Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../ui/alert-dialog";
import { Button } from "../../ui/button";
import { blockTitle, isSectionBlock } from "../../site-pages/block-meta";
import { BLOCK_CHANGED_TEXT } from "../../../lib/site-builder/block-errors";
import { SectionBlockEditor } from "../section/SectionBlockEditor";
import { UnsavedGuard } from "../../site-view/UnsavedGuard";
import { useBuilder } from "../builder-context";
import { LeaveConfirmDialog } from "./LeaveConfirmDialog";
import { usePanelLeaveGuard } from "../../../hooks/site-builder/usePanelLeaveGuard";
import type { SitePageBlock } from "../../../lib/api-site-pages";

/** То же содержимое блока (новый объект после перечитывания страницы). */
function sameContent(a: SitePageBlock, b: SitePageBlock): boolean {
  return a.id === b.id && JSON.stringify([a.payload, a.appearance]) === JSON.stringify([b.payload, b.appearance]);
}

export function BlockPanel({ block }: { block: SitePageBlock }) {
  const b = useBuilder();
  const [dirty, setDirty] = useState(false);
  const [shown, setShown] = useState(block);
  const controller = useRef<{ save: () => Promise<boolean> } | null>(null);
  const save = useCallback(async () => (controller.current ? controller.current.save() : true), []);
  // Переход разрешён окном «Сохранить / Не сохранять» — второе окно не нужно (`passed`).
  const { leave, passed } = usePanelLeaveGuard(dirty, save);
  // После своего сохранения следующая версия блока с сервера — своя.
  const acceptNext = useRef(false);
  // Блок изменили в другом месте, а в форме правки: правки остаются, окно-пояснение.
  const [changedNotice, setChangedNotice] = useState(false);
  const acknowledged = useRef<string | null>(null);

  const { pages } = b;
  const onSaved = useCallback(() => {
    acceptNext.current = true;
    void pages.reloadPage();
    pages.bumpCanvas();
  }, [pages]);
  // 409 block_changed при сохранении: свежая страница, правки в форме, одно окно.
  const onConflict = useCallback(() => {
    void pages.reloadPage();
    setChangedNotice(true);
  }, [pages]);

  const latest = useRef({ shown, dirty });
  latest.current = { shown, dirty };
  useEffect(() => {
    const { shown: current, dirty: hasEdits } = latest.current;
    if (sameContent(current, block)) {
      acceptNext.current = false;
      return;
    }
    if (!hasEdits || acceptNext.current) {
      acceptNext.current = false;
      setShown(block);
      return;
    }
    const key = JSON.stringify([block.payload, block.appearance]);
    if (acknowledged.current === key) return;
    acknowledged.current = key;
    setChangedNotice(true);
  }, [block]);

  return (
    <>
      {!isSectionBlock(shown) ? (
        <div className="flex flex-col items-start gap-3">
          <p className="type-body text-on-surface-variant">{blockTitle(shown)}: этот вид блока больше не поддерживается и на сайте не показывается.</p>
          <Button variant="outline" className="text-destructive" onClick={() => void b.blocks.remove(shown)}>
            <Trash2 aria-hidden />
            Удалить блок
          </Button>
        </div>
      ) : (
        <SectionBlockEditor key={shown.id} block={shown} onDirtyChange={setDirty} controller={controller} onSaved={onSaved} onConflict={onConflict} />
      )}
      <AlertDialog open={changedNotice} onOpenChange={(open) => (!open ? setChangedNotice(false) : null)}>
        <AlertDialogContent fullScreen className="max-w-md gap-3">
          <AlertDialogHeader>
            <AlertDialogTitle>Блок изменили</AlertDialogTitle>
            <AlertDialogDescription>{BLOCK_CHANGED_TEXT}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button onClick={() => setChangedNotice(false)}>Понятно</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <UnsavedGuard when={dirty} blockSearch skip={() => passed.current} />
      <LeaveConfirmDialog leave={leave} title="Блок не сохранён" description="Есть правки, которые ещё не на странице. Сохранить их перед переходом?" />
    </>
  );
}
